/**
 * H타입 - 한글 텍스트 입력 컴포넌트
 * 텍스트 입력 필드를 사용한 단답형 입력
 */

import React, { useState } from 'react';
import { PixelButton } from '../common/PixelButton';
import { PixelInput } from '../common/PixelInput';

/**
 * HandwritingInput Props
 */
interface HandwritingInputProps {
  /** 답안 제출 콜백 */
  onSubmit: (answer: string) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * H타입 한글 텍스트 입력 컴포넌트
 *
 * 플로우:
 * 1. 텍스트 필드에 한글 입력 (운영체제별 네이티브 키보드)
 * 2. 제출 버튼 클릭 또는 Enter 키
 * 3. 답안 제출 → 채점
 */
export const HandwritingInput: React.FC<HandwritingInputProps> = ({
  onSubmit,
  disabled = false,
}) => {
  const [answer, setAnswer] = useState('');

  /**
   * 제출 핸들러
   */
  const handleSubmit = () => {
    if (!answer.trim() || disabled) return;

    onSubmit(answer.trim());

    // 제출 후 초기화
    setAnswer('');
  };

  /**
   * Enter 키 입력 시 제출
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && answer.trim() && !disabled) {
      handleSubmit();
    }
  };

  /**
   * 클리어 핸들러
   */
  const handleClear = () => {
    setAnswer('');
  };

  return (
    <div className="space-y-3">
      {/* 안내 메시지 */}
      <p className="text-center font-pixel text-xs text-gray-400">
        한글로 답을 입력하세요 (키보드 사용)
      </p>

      {/* 텍스트 입력 필드 */}
      <div className="relative">
        <PixelInput
          type="text"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="답 입력..."
          disabled={disabled}
          className="w-full text-center text-xl py-3 font-pixel"
          autoFocus
          maxLength={50}
        />
      </div>

      {/* 입력 상태 표시 */}
      {answer && (
        <div className="text-center">
          <p className="font-pixel text-sm text-[#9bbc0f]">
            입력: {answer}
          </p>
          <p className="font-pixel text-xs text-gray-500">
            {answer.length}/50자
          </p>
        </div>
      )}

      {/* 버튼 그룹 */}
      <div className="grid grid-cols-2 gap-2">
        {/* 지우기 버튼 */}
        <PixelButton
          variant="secondary"
          size="small"
          onClick={handleClear}
          disabled={!answer || disabled}
        >
          지우기
        </PixelButton>

        {/* 제출 버튼 */}
        <PixelButton
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={!answer.trim() || disabled}
        >
          제출
        </PixelButton>
      </div>

      {/* 힌트 */}
      <p className="text-center font-pixel text-xs text-gray-500">
        Enter 키를 눌러도 제출됩니다
      </p>
    </div>
  );
};
